import NumberFlow from '@number-flow/react';
import { Button } from '@savvy-kit/components/button';
import { Skeleton } from '@savvy-kit/components/skeleton';
import { Plus, X } from 'lucide-react';
import { graphql } from 'src/gql';

import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';

import { useTaxOverviewParams } from './tax-overview-url-state';

export const TaxEnrollmentTableHeadersQuery = graphql(/* GraphQL */ `
  query TaxEnrollmentTableHeaders {
    taxEnrollmentsTableHeaders {
      requireAdvisorActionCount
      requireClientActionCount
      waitingOnThirdPartyCount
    }
  }
`);

type TaxStat = {
  label: string;
  value: number;
  isActive: boolean;
  onToggle: () => void;
};

export default function TaxTableCards() {
  const { data, loading } = useQueryWithErrorLogging(TaxEnrollmentTableHeadersQuery);
  const { params, setRequireAdvisorActionFilter, setRequireClientActionFilter, setWaitingOnThirdPartyFilter } =
    useTaxOverviewParams();

  const cards: TaxStat[] = [
    {
      label: 'Require Advisor Action',
      value: data?.taxEnrollmentsTableHeaders.requireAdvisorActionCount ?? 0,
      isActive: !!params.requireAdvisorActionFilter,
      onToggle: () => setRequireAdvisorActionFilter(params.requireAdvisorActionFilter !== true),
    },
    {
      label: 'Require Client Action',
      value: data?.taxEnrollmentsTableHeaders.requireClientActionCount ?? 0,
      isActive: !!params.requireClientActionFilter,
      onToggle: () => setRequireClientActionFilter(params.requireClientActionFilter !== true),
    },
    {
      label: 'Waiting on Third Party',
      value: data?.taxEnrollmentsTableHeaders.waitingOnThirdPartyCount ?? 0,
      isActive: !!params.waitingOnThirdPartyFilter,
      onToggle: () => setWaitingOnThirdPartyFilter(params.waitingOnThirdPartyFilter !== true),
    },
  ];

  if (loading || !data) {
    return (
      <div className="grid grid-cols-3 overflow-hidden rounded-md border border-border-light">
        {Array.from({ length: 3 }).map((_, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} className={`bg-white px-5 py-4 ${i < 2 ? 'border-r border-border-light' : ''}`}>
            <Skeleton className="mb-2 h-3 w-28" />
            <Skeleton className="h-7 w-12" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-3 overflow-hidden rounded-md border border-border-light">
      {cards.map((card, i) => {
        const isLast = i === cards.length - 1;
        const borderClass = isLast ? '' : 'border-r border-border-light';

        return (
          <div
            key={card.label}
            className={`flex min-w-0 items-center justify-between bg-white px-5 py-4 ${borderClass}`}
          >
            <div className="flex min-w-0 flex-col">
              <p className="mb-1 truncate text-[11px] font-semibold uppercase tracking-wider text-secondary">
                {card.label}
              </p>
              <p className="text-2xl font-semibold leading-tight">
                <NumberFlow value={card.value} />
              </p>
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="text-secondary"
              icon={card.isActive ? <X /> : <Plus />}
              onClick={card.onToggle}
            >
              {card.isActive ? 'Remove filter' : 'Filter'}
            </Button>
          </div>
        );
      })}
    </div>
  );
}
